import * as Sentry from '@sentry/react';
import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import { apiEndpoint, isStandinDeployment } from '../client';
import {
  ActivationState,
  applyState,
  fromWire,
  heldState,
  readHeldStates,
  subscribeToActivationState,
} from '../common/utils/activationStateCache';
import { localServerAuthHeaders } from '../common/utils/offlineMode/localServerAuthHeaders';

export type ActivationStateResponse = {
  status: number;
  json: () => Promise<unknown>;
};

export type ActivationStateFetchInit = {
  headers: Record<string, string>;
  cache: 'no-store';
  signal: AbortSignal;
};

export type ActivationStateFetch = (
  url: string,
  init: ActivationStateFetchInit
) => Promise<ActivationStateResponse>;

export type ActivationStateStoreClient = {
  on(event: string, handler: (...args: never[]) => void): unknown;
  removeListener(event: string, handler: (...args: never[]) => void): unknown;
  httpClient?: { getAuthHeaders?: () => Record<string, string> };
};

export type ActivationStateStoreStores = {
  messengerStore?: { currentOrganizationId?: string | null };
};

export type ActivationStateStoreDeps = {
  client: ActivationStateStoreClient;
  stores: ActivationStateStoreStores;
  fetchImpl?: ActivationStateFetch;
  isStandinDeployment?: boolean;
  apiBaseUrl?: string;
};

type NetworkStatusChange = {
  networkStatus?: string;
  previousStatus?: string;
};

const PULL_TIMEOUT_MS = 10000;

function unwrapReply(body: unknown): Record<string, unknown> | undefined {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return undefined;

  const reply = (body as Record<string, unknown>).reply;
  if (typeof reply !== 'object' || reply === null || Array.isArray(reply)) return undefined;

  return reply as Record<string, unknown>;
}

const defaultFetch: ActivationStateFetch = (url, init) => global.fetch(url, init);

export default class ActivationStateStore {
  @observable statesByOrg: Record<string, ActivationState> = {};

  @observable lastSuccessfulPullAt: Record<string, number> = {};

  client: ActivationStateStoreClient;
  stores: ActivationStateStoreStores;
  fetchImpl: ActivationStateFetch;
  isStandinDeployment: boolean;
  apiBaseUrl?: string;
  _disposeOrgChangeReaction: (() => void) | null = null;
  _disposeActivationCacheSubscription: (() => void) | null = null;

  constructor({
    client,
    stores,
    fetchImpl = defaultFetch,
    isStandinDeployment: isStandin = isStandinDeployment,
    apiBaseUrl = apiEndpoint?.baseUrl,
  }: ActivationStateStoreDeps) {
    makeObservable(this);
    this.client = client;
    this.stores = stores;
    this.fetchImpl = fetchImpl;
    this.isStandinDeployment = isStandin === true;
    this.apiBaseUrl = apiBaseUrl;
  }

  _subscribeToActivationCache() {
    if (this._disposeActivationCacheSubscription) return;
    this._disposeActivationCacheSubscription = subscribeToActivationState(
      this._handleActivationStateWrite
    );
  }

  mounted() {
    this.hydrateFromCache();

    this.client.on('visibilityChange', this._handleVisibilityChange);
    this.client.on('networkStatus:change', this._handleNetworkStatusChange);
    this._subscribeToActivationCache();

    this._disposeOrgChangeReaction = reaction(
      () => this.stores.messengerStore?.currentOrganizationId ?? null,
      (orgToken: string | null) => {
        if (!orgToken) return;
        void this.pull('organizationChange');
      }
    );

    void this.pull('mounted');
  }

  dispose() {
    this.client.removeListener('visibilityChange', this._handleVisibilityChange);
    this.client.removeListener('networkStatus:change', this._handleNetworkStatusChange);
    this._disposeOrgChangeReaction?.();
    this._disposeOrgChangeReaction = null;
    this._disposeActivationCacheSubscription?.();
    this._disposeActivationCacheSubscription = null;
  }

  _handleActivationStateWrite = () => {
    this.hydrateFromCache();
  };

  @action('ActivationStateStore.hydrateFromCache') hydrateFromCache() {
    this.statesByOrg = readHeldStates();
  }

  isOfflineModeActive = (orgToken: string): boolean => {
    const state = this.statesByOrg[orgToken] ?? heldState(orgToken);
    return state?.isActive === true;
  };

  lastSuccessfulPull = (orgToken: string): number | undefined =>
    this.lastSuccessfulPullAt[orgToken];

  @action('ActivationStateStore.recordSuccessfulPull') _recordSuccessfulPull(orgToken: string) {
    this.lastSuccessfulPullAt = { ...this.lastSuccessfulPullAt, [orgToken]: Date.now() };
  }

  async pull(reason: string): Promise<void> {
    if (!this.isStandinDeployment) return;
    const orgToken = this.stores.messengerStore?.currentOrganizationId;
    if (!orgToken) return;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PULL_TIMEOUT_MS);

    try {
      let response: ActivationStateResponse;
      try {
        response = await this.fetchImpl(this._endpointFor(orgToken), {
          headers: this._authHeaders(),
          cache: 'no-store',
          signal: controller.signal,
        });
      } catch {
        return;
      }

      if (response.status !== 200) return;
      this._recordSuccessfulPull(orgToken);

      try {
        const reply = unwrapReply(await response.json());
        if (!reply) return;

        const incoming = fromWire(orgToken, reply);
        if (!incoming) return;

        if (!applyState(incoming)) return;

        runInAction(() => {
          this.statesByOrg = { ...this.statesByOrg, [orgToken]: incoming };
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        Sentry.logger.error(
          Sentry.logger.fmt`[OfflineMode] Failed to process the activation state reply`,
          { err, organizationId: orgToken, reason }
        );
      }
    } finally {
      clearTimeout(timer);
    }
  }

  _handleVisibilityChange = (_event: unknown, state: unknown) => {
    if (String(state).toUpperCase() !== 'VISIBLE') return;
    void this.pull('visibilityChange');
  };

  _handleNetworkStatusChange = ({ networkStatus, previousStatus }: NetworkStatusChange = {}) => {
    if (networkStatus !== 'ONLINE' || previousStatus === 'ONLINE') return;
    void this.pull('networkStatus:change');
  };

  _authHeaders(): Record<string, string> {
    return localServerAuthHeaders(this.client.httpClient);
  }

  _endpointFor(orgToken: string): string {
    const base = (this.apiBaseUrl || window.location.origin).replace(/\/+$/, '');
    return `${base}/v2/organization/${encodeURIComponent(orgToken)}/offline-mode`;
  }
}
